import { useEffect, useMemo, useState } from 'react'
import { DndContext, DragEndEvent, closestCorners, useSensor, useSensors, MouseSensor, closestCenter } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { List } from '@mui/material'

import { PatientQueueEntry } from '@/core/models/patient-queue.model'

import { EmptyQueue } from './EmptyQueue'
import { SortablePatientItem } from './SortablePatientItem'
import { useReorderQueuedPatientsMutation } from '@/core/services/patient-queue'

type SortablePatientsProps = {
  patients: PatientQueueEntry[]
}

export function SortablePatientsList({ patients: fetchedPatients }: SortablePatientsProps) {
  const sensors = useSensors(useSensor(MouseSensor, {}))
  const [reorderPatients] = useReorderQueuedPatientsMutation()

  const [patients, setPatients] = useState(fetchedPatients)

  const patientIds = useMemo(() => patients.map((patient) => patient.patient._id), [patients])

  // sync internal patients with fetched patients
  // normally this should not be needed, but for some reason if we render fetchedPatients directly, the active item jumps when dropping
  useEffect(() => {
    setPatients(fetchedPatients)
  }, [fetchedPatients])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const sourceIndex = patients.findIndex(({ patient }) => patient._id === active.id)
    const destinationIndex = patients.findIndex(({ patient }) => patient._id === over.id)

    if (sourceIndex === -1 || destinationIndex === -1) return

    const movedPatients = arrayMove(patients, sourceIndex, destinationIndex)
    setPatients(movedPatients.map((patient, index) => ({ ...patient, position: index })))
    reorderPatients(
      movedPatients.map((patient, index) => ({
        patientId: patient.patient._id,
        position: index,
      }))
    )
  }

  if (!patients.length) {
    return <EmptyQueue />
  }

  return (
    <DndContext
      autoScroll={false}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={patientIds} strategy={verticalListSortingStrategy}>
        <List sx={{ p: 0 }}>
          {patients.map((patientQueueEntry) => (
            <SortablePatientItem key={patientQueueEntry.patient._id} patientQueueEntry={patientQueueEntry} />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  )
}
